import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Grid, Input } from 'semantic-ui-react';
import { checkPrinter, print } from 'services/printer.service';
import { useAlert } from 'context/AlertProvider';
import { useZebra } from 'context/ZebraProvider';
import './preparation-order-print-label-form.scss';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import {
  confirmPrint,
  getContent as getShipmentLabelContent,
} from 'services/shipment-labels.service';
import { getContent as getExternalLabelContent } from 'services/external-labels.service';
import { useApp } from 'context/AppProvider';

export default function PreparationOrderPrintLabelForm({
  isPrinting,
  setIsPrinting,
  preparationOrder,
  placeholder = 'Escanea o introduce EAN',
  onLabelConfirmed = () => {},
}) {
  const { getInputFocus } = useApp();
  const [EANToPrint, setEANToPrint] = useState('');
  const pistolInput = useRef();
  const resetInput = useCallback(() => {
    setEANToPrint('');
    pistolInput?.current?.focus();
    setIsPrinting({ message: 'Imprimiendo…', isActive: false });
  }, [setEANToPrint, pistolInput, setIsPrinting]);
  const alert = useAlert();
  const zebra = useZebra();

  const renderPrintError = (message, cb = () => {}) => {
    alert.current.error(message, () => {
      setIsPrinting({ message: 'Imprimiendo…', isActive: false });
      resetInput();
      cb();
    });
  };

  useEffect(() => {
    pistolInput?.current?.focus();
  }, [getInputFocus]);

  return (
    <Grid centered columns={2}>
      <Grid.Column>
        <Form>
          <Form.Field className="preparation-order-print-label-form">
            <label>Imprimir etiquetas</label>
            <Input
              ref={pistolInput}
              onChange={(event, data) => {
                setEANToPrint(data.value);
              }}
              value={EANToPrint}
              autoFocus
              placeholder={placeholder}
              size="big"
              action={{
                color: 'blue',
                labelPosition: 'right',
                disabled: isPrinting || !EANToPrint,
                loading: isPrinting,
                icon: 'print',
                content: 'Imprimir',
                onClick: async () => {
                  setIsPrinting({
                    message: 'Imprimiendo…',
                    isActive: true,
                  });
                  const isAvailable = zebra ? await checkPrinter(zebra) : false;
                  if (!isAvailable) {
                    renderPrintError(
                      'Parece que la impresora por defecto no esta disponible, revise que esta encendida y conectada e intentelo de nuevo.',
                    );
                    return;
                  }

                  const EANExists = preparationOrder?.table.some(
                    (item) => item.ean === EANToPrint,
                  );

                  if (!EANExists) {
                    renderPrintError(
                      'La referencia no pertenece a esta orden de preparación',
                    );
                    return;
                  }

                  const EANHasRemainingLabels = !!(
                    EANExists && preparationOrder?.counter[EANToPrint]
                  );

                  if (!EANHasRemainingLabels) {
                    renderPrintError(
                      'Las etiquetas de esa referencia ya han sido impresas',
                    );
                    return;
                  }

                  try {
                    const label = preparationOrder?.labels.find(
                      (label) => label.ean === EANToPrint,
                    );
                    const {
                      data: { content: labelContent },
                    } = await getShipmentLabelContent(label.label_id);
                    await print(zebra, labelContent);
                    await confirmPrint(label.label_id);
                    if (!!label.external_label_id) {
                      try {
                        setIsPrinting({
                          message: 'Imprimiendo etiqueta externa…',
                          isActive: true,
                        });
                        const {
                          data: { content: externalLabelcontent },
                        } = await getExternalLabelContent(
                          label.external_label_id,
                        );
                        await print(zebra, externalLabelcontent);
                      } catch (error) {
                        alert.current.error(getMessageFromAxiosError(error));
                      }
                    }
                    onLabelConfirmed();
                    resetInput();
                  } catch (error) {
                    alert.current.error(
                      getMessageFromAxiosError(error),
                      resetInput,
                    );
                  }
                },
              }}
            />
          </Form.Field>
        </Form>
      </Grid.Column>
    </Grid>
  );
}
